import { useState, useContext } from 'react';
import {
    Typography,
    TextField,
    Button,
    OutlinedInput,
    IconButton,
    InputAdornment,
    Autocomplete,
    InputLabel
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import { CountryCodes } from './CountryCodes';
import { Designations } from './Designations';
import { StepBanner } from './StepBanner';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import { KYC, Analytics } from 'lib/api';
import { AuthContext } from 'context/auth-context';
import StatusSnackbar from 'components/shared/StatusSnackbar';
import CircularComponent from 'components/shared/CircularComponent';
import { US_SSN_REGEX, US_SSN_REGEX_TEST, US_POSTAL_CODE_REGEX, ENVIRONMENT } from 'helpers/constants';
import dayjs from 'dayjs';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { onlyNumber } from 'helpers/signInValidation';
import { KYX_ANALYTICS_EVENTS } from './StartKYX';
import { isUsaZip } from 'helpers/zips';

const { NODE_ENV } = process.env;
const useStyles = makeStyles({
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '600px',
        height: '100%',
        rowGap: '1em'
    },
    content: {
        margin: '2em',
        width: '40em'
    },
    contentTitle: {
        fontWeight: '700!important'
    },
    informationBox: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2,1fr)',
        marginTop: '1em',
        columnGap: '1em'
    },
    informationOptions: {
        marginTop: '1em',
        display: 'flex',
        justifyContent: 'space-between'
    },
    next: {
        color: 'white!important',
        borderRadius: '25px!important',
        marginLeft: '1em',
        padding: '.5em 3em!important'
    },
    resize: {
        fontSize: '13px!important'
    }
});

export const KYXThirdStep = ({ handleClose, decreasePage, incrementPage, activePage, handleInputChange, formValues }) => {
    const classes = useStyles();
    const [openAlert, setOpenAlert] = useState(false);
    const [needsAction, setNeedsAction] = useState(false);
    const [label, setLabel] = useState('');
    const { phoneNum, setPhoneNum } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);
    const [showWrongFormat, setShowWrongFormat] = useState(false);
    const [stateValue, setStateValue] = useState('');
    const [designationValue, setDesignationValue] = useState('');
    const [ageAlert, setAgeAlert] = useState(false);
    const [dob, setDob] = useState(null);
    const [showSSN, setShowSSN] = useState(false);
    const [postalError, setPostalError] = useState(false);
    const ssnValidation = NODE_ENV === ENVIRONMENT.DEVELOPMENT ? US_SSN_REGEX_TEST : US_SSN_REGEX;

    const incrementStep = () => {};

    const sendInfo = () => {
        if (!ssnValidation.test(formValues.ssn)) {
        }
        if (isUsaZip(formValues.postalCode) && US_POSTAL_CODE_REGEX.test(formValues.postalCode)) {
            setPostalError(false);
        } else {
            setPostalError(true);
            return;
        }
        if (!dayjs(dob).isValid()) {
            return;
        }
        let val = formValues;
        val.phoneNumber = val.phoneNumber.replace('+1', '');
        val.dateOfBirth = dayjs(dob).format('YYYY-MM-DD');

        let payload = {
            ...val,
            jobTitle: designationValue,
            address: {
                line1: val.line1,
                line2: val.line2,
                city: val.city,
                state: stateValue,
                country: val.country,
                postalCode: val.postalCode
            }
        };

        ['line1', 'line2', 'city', 'state', 'postalCode', 'country', 'phoneNumber', 'code'].forEach(prop => delete payload[prop]);

        if (payload !== undefined && val !== undefined) {
            setLoading(true);
            KYC.createKYC(payload)
                .then(response => {
                    window.dataLayer.push({
                        event: 'virtual-card-kyc-success'
                    });
                    const body = {
                        step: KYX_ANALYTICS_EVENTS.STEP_3,
                        eventData: formValues
                    };
                    Analytics.createKYXEvent(body).catch(error => console.error(error));
                    setLoading(false);
                    incrementPage();
                })
                .catch(error => {
                    if (error.response.data.type === 'KYC/PhoneNumberExists') {
                        setLabel('Phone number already exists');
                        setPhoneNum('');
                        setNeedsAction(true);
                        setOpenAlert(true);
                        return;
                        /*decreasePage();*/
                    }
                    if (error.response.data.type === 'Validation/ValidationError') {
                        setLabel('All fields are required');
                        setOpenAlert(true);
                        return;
                    }
                    if (error.response.data.type === 'KYC/InvalidIdNumber') {
                        setLabel('Invalid SSN number');
                        setOpenAlert(true);
                        return;
                        /*decreasePage();*/
                    }

                    setLabel(error.response.data.message);
                    setOpenAlert(true);
                    setLoading(false);
                });
        }
    };

    const verifyFormat = () => {
        let ssn = formValues.ssn;

        if (!ssnValidation.test(ssn)) {
            setShowWrongFormat(true);
        } else {
            setShowWrongFormat(false);
        }
    };

    const verifyPostalCodeFormat = () => {
        let postalCode = formValues.postalCode;
        if (isUsaZip(postalCode) && US_POSTAL_CODE_REGEX.test(postalCode)) {
            setPostalError(false);
        } else {
            setPostalError(true);
        }
    };

    const VerifyAge = date => {
        const dDate = dayjs(date);
        if (!dDate.isValid()) return 0;
        const age = dayjs().diff(dayjs(date), 'year');
        if (age > 101) return 0;
        return age;
    };

    const convertDate = date => {
        let age = VerifyAge(date);
        if (age >= 18) {
            setAgeAlert(false);
            let validDate = dayjs(date).isValid();
            if (validDate) {
                setDob(date);
            }
        } else {
            setAgeAlert(true);
            setDob(null);
        }
    };

    const disableNext = () => {
        return !(
            designationValue !== '' &&
            formValues.line1 !== '' &&
            formValues.city !== '' &&
            stateValue !== '' &&
            ssnValidation.test(formValues.ssn) &&
            isUsaZip(formValues.postalCode) &&
            dayjs(dob).isValid() &&
            US_POSTAL_CODE_REGEX.test(formValues.postalCode)
        );
    };

    return (
        <Box className={classes.dialogBody}>
            <StepBanner activeStep={activePage} incrementStep={() => incrementStep()} />
            <Box className={classes.content}>
                <Typography
                    variant="h5"
                    className={classes.contentTitle}
                    sx={{
                        fontSize: {
                            lg: '23px',
                            md: '23px'
                        },
                        fontWeight: '700',
                        color: '#182D55'
                    }}>
                    Basic Information
                </Typography>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontSize: {
                            lg: '14px',
                            md: '14px'
                        },
                        fontWeight: '500',
                        color: '#A8B6C1'
                    }}>
                    This will help us verify your identity quickly.
                </Typography>
                <Box style={{ marginTop: '2em' }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <InputLabel
                            sx={{
                                color: '#225384',
                                fontWeight: 600,
                                marginBottom: '.5em',
                                fontSize: {
                                    lg: '13px',
                                    md: '13px'
                                }
                            }}>
                            Date of birth
                        </InputLabel>
                        <DesktopDatePicker
                            placeholder="Date of birth"
                            inputFormat="MM/dd/yyyy"
                            maxDate={new Date()}
                            value={dob}
                            onChange={e => convertDate(e)}
                            renderInput={params => <TextField {...params} size="small" style={{ width: '100%' }} />}
                        />
                    </LocalizationProvider>
                </Box>
                {ageAlert ? (
                    <Typography sx={{ color: 'red', fontSize: '.75rem' }}>You must be 18 years or older to register</Typography>
                ) : null}
                <Box style={{ marginTop: '0.5em' }}>
                    <InputLabel
                        sx={{
                            color: '#225384',
                            fontWeight: 600,
                            marginBottom: '.5em',
                            fontSize: {
                                lg: '13px',
                                md: '13px'
                            }
                        }}>
                        Job Title
                    </InputLabel>
                    <Autocomplete
                        disablePortal
                        id="jobTitle"
                        options={Designations}
                        sx={{ gridColumn: '2/3' }}
                        renderInput={params => <TextField {...params} size="small" placeholder="Select job title" />}
                        getOptionLabel={option => option.label}
                        onChange={(e, newVal) => {
                            if (newVal) {
                                setDesignationValue(newVal.label);
                            } else {
                                setDesignationValue('');
                            }
                        }}
                    />
                </Box>
                <Box style={{ marginTop: '0.5em' }}>
                    <InputLabel
                        sx={{
                            color: '#225384',
                            fontWeight: 600,
                            marginBottom: '.5em',
                            fontSize: {
                                lg: '13px',
                                md: '13px'
                            }
                        }}>
                        Social Security Number (SSN)
                    </InputLabel>
                    <OutlinedInput
                        error={showWrongFormat}
                        name="ssn"
                        id="ssn"
                        size="small"
                        value={formValues?.ssn}
                        onBlur={() => verifyFormat()}
                        className={classes.inputSSNForm}
                        onChange={e => {
                            if (onlyNumber(e.target.value)) return handleInputChange(e);
                        }}
                        required
                        inputProps={{ maxLength: 9 }}
                        InputLabelProps={{
                            style: {
                                fontSize: {
                                    lg: '13px',
                                    md: '13px'
                                }
                            }
                        }}
                        InputProps={{
                            style: {
                                fontSize: {
                                    lg: '13px',
                                    md: '13px'
                                }
                            }
                        }}
                        sx={{
                            width: {
                                lg: '60%',
                                md: '60%',
                                sm: '100%',
                                xs: '100%'
                            },
                            gridColumn: {
                                lg: '',
                                md: '',
                                sm: '1/3',
                                xs: '1/3'
                            }
                        }}
                        type={showSSN ? 'text' : 'password'}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton aria-label="Toggle SSN visibility" onClick={() => setShowSSN(!showSSN)} edge="end">
                                    {!showSSN ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        }
                        style={{ width: '100%' }}
                    />
                    {showWrongFormat ? (
                        <Typography sx={{ color: 'red', fontSize: '.75rem' }}>Please, enter a correct SSN.</Typography>
                    ) : null}
                </Box>
                <Box className={classes.informationBox}>
                    <InputLabel
                        sx={{
                            color: '#225384',
                            fontWeight: 600,
                            marginBottom: '.5em',
                            fontSize: {
                                lg: '13px',
                                md: '13px'
                            }
                        }}>
                        Residential Address
                    </InputLabel>
                    <TextField
                        onChange={handleInputChange}
                        id="line1"
                        placeholder="Street Line 1"
                        type="text"
                        defaultValue=""
                        size="small"
                        sx={{ gridColumn: '1/3' }}
                    />
                    <TextField
                        onChange={handleInputChange}
                        id="line2"
                        placeholder="Street Line 2"
                        type="text"
                        defaultValue=""
                        size="small"
                        sx={{ gridColumn: '1/3', marginTop: '1em' }}
                    />
                    <TextField
                        onChange={handleInputChange}
                        id="city"
                        placeholder="City"
                        type="text"
                        defaultValue=""
                        size="small"
                        sx={{ gridColumn: '1/2', marginTop: '1em' }}
                    />
                    <Autocomplete
                        disablePortal
                        id="state"
                        options={CountryCodes}
                        sx={{ gridColumn: '2/3', marginTop: '1em' }}
                        renderInput={params => <TextField {...params} size="small" placeholder="State" />}
                        getOptionLabel={option => option.value}
                        onChange={(e, newVal) => {
                            if (newVal) {
                                setStateValue(newVal.label);
                            } else {
                                setStateValue('');
                            }
                        }}
                    />
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <TextField
                            error={postalError}
                            onChange={handleInputChange}
                            id="postalCode"
                            onBlur={verifyPostalCodeFormat}
                            placeholder="Zip Code"
                            type="number"
                            defaultValue=""
                            size="small"
                            sx={{ gridColumn: '1/2', marginTop: '1em' }}
                            onInput={e => {
                                e.target.value = e.target.value.length > 5 ? e.target.value.slice(0, 5) : e.target.value;
                            }}
                            InputProps={{
                                classes: {
                                    input: classes.resize
                                }
                            }}
                        />
                        {postalError ? (
                            <Typography sx={{ color: 'red', fontSize: '.75rem' }}>
                                This zip code is not valid, a 5-digit U.S. zip code is required.
                            </Typography>
                        ) : null}
                    </Box>
                    <TextField
                        onChange={handleInputChange}
                        //id="region"
                        placeholder="Usa Only"
                        type="text"
                        defaultValue="USA"
                        disabled
                        size="small"
                        InputProps={{
                            classes: {
                                input: classes.resize
                            }
                        }}
                        sx={{ gridColumn: '2/3', marginTop: '1em' }}
                    />
                </Box>
                <Box className={classes.informationOptions}>
                    {phoneNum !== ' ' || label !== ' ' ? (
                        <div>&nbsp;</div>
                    ) : (
                        <Button variant="text" onClick={() => decreasePage()}>
                            Back
                        </Button>
                    )}
                    {loading ? (
                        <CircularComponent />
                    ) : (
                        <Button variant="contained" disabled={disableNext()} onClick={() => sendInfo()}>
                            Next
                        </Button>
                    )}
                </Box>
            </Box>
            <StatusSnackbar
                label={label}
                severity={'error'}
                open={openAlert}
                setOpenAlert={setOpenAlert}
                needsAction={needsAction}
                decreasePage={decreasePage}
            />
        </Box>
    );
};
